import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Clock3,
  IndianRupee,
  Loader2,
  ShieldCheck,
  Users,
} from 'lucide-react';

import { usePlatformStats, useTenantIsolation } from '@/api/hooks/useSuperadmin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate, formatDateTime, moneyCompact } from '@/lib/utils';

function OverviewSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
      <Skeleton className="h-64" />
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  tone = 'default',
}: {
  label: string;
  value: string | number;
  icon: typeof Building2;
  tone?: 'default' | 'warning' | 'danger';
}) {
  return (
    <Card className="flex items-center gap-3 p-4">
      <div
        className={
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ' +
          (tone === 'danger'
            ? 'bg-status-expired/15 text-status-expired'
            : tone === 'warning'
              ? 'bg-status-low/15 text-status-low'
              : 'bg-primary/15 text-primary')
        }
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="tabular text-xl font-semibold leading-tight">{value}</p>
        <p className="truncate text-xs text-muted-foreground">{label}</p>
      </div>
    </Card>
  );
}

export function OverviewTab() {
  const { data, isLoading, isError } = usePlatformStats();
  const [checkEnabled, setCheckEnabled] = useState(false);
  const isolation = useTenantIsolation(checkEnabled);

  if (isLoading) return <OverviewSkeleton />;

  if (isError || !data) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Couldn't load platform stats. Try again shortly.
        </CardContent>
      </Card>
    );
  }

  const { totals, planBreakdown, signupTrend, mrr, newPharmacies30d, trialEndingSoon } = data;
  const trendData = signupTrend.map((p) => ({ date: p.date, count: p.count }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Pharmacies" value={totals.totalPharmacies} icon={Building2} />
        <StatCard label="Active" value={totals.activePharmacies} icon={CheckCircle2} />
        <StatCard
          label="Suspended"
          value={totals.suspendedPharmacies}
          icon={AlertTriangle}
          tone={totals.suspendedPharmacies > 0 ? 'danger' : 'default'}
        />
        <StatCard label="Total users" value={totals.totalUsers} icon={Users} />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="MRR" value={moneyCompact(mrr)} icon={IndianRupee} />
        <StatCard label="New pharmacies (30d)" value={newPharmacies30d} icon={Building2} />
        <StatCard
          label="Trials ending in 7d"
          value={trialEndingSoon}
          icon={Clock3}
          tone={trialEndingSoon > 0 ? 'warning' : 'default'}
        />
        <StatCard
          label="Pending claims"
          value={totals.pendingClaims}
          icon={AlertTriangle}
          tone={totals.pendingClaims > 0 ? 'warning' : 'default'}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Signups, last 30 days</CardTitle>
          </CardHeader>
          <CardContent>
            {trendData.length === 0 ? (
              <div className="flex h-56 items-center justify-center text-sm text-muted-foreground">
                No signups in this window.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={trendData} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v: string) => formatDate(v).slice(0, 5)}
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    width={28}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.[0]) return null;
                      return (
                        <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-lg">
                          <p className="font-medium">{formatDate(label as string)}</p>
                          <p className="tabular mt-0.5">{payload[0].value} signup(s)</p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="count" fill="#34C39B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Plan breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(planBreakdown).map(([plan, count]) => (
              <div key={plan} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{plan}</span>
                <span className="tabular font-medium">{count}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base">Tenant isolation check</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCheckEnabled(true)}
            disabled={isolation.isFetching}
          >
            {isolation.isFetching && <Loader2 className="h-4 w-4 animate-spin" />}
            Run check
          </Button>
        </CardHeader>
        <CardContent>
          {!checkEnabled ? (
            <p className="text-sm text-muted-foreground">
              Sweeps every tenant-scoped table for rows whose pharmacy no longer exists.
            </p>
          ) : isolation.isLoading ? (
            <Skeleton className="h-8" />
          ) : isolation.data ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {isolation.data.healthy ? (
                  <Badge variant="ok" className="gap-1">
                    <ShieldCheck className="h-3 w-3" /> Healthy
                  </Badge>
                ) : (
                  <Badge variant="out">{isolation.data.orphans.length} table(s) with orphan rows</Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  {isolation.data.pharmacyCount} pharmacies · checked {formatDateTime(isolation.data.checkedAt)}
                </span>
              </div>
              {!isolation.data.healthy && (
                <div className="space-y-1 text-sm">
                  {isolation.data.orphans.map((o) => (
                    <div key={o.table} className="flex justify-between rounded-md bg-secondary/50 px-3 py-1.5">
                      <span>{o.table}</span>
                      <span className="tabular text-status-expired">{o.orphanRows} orphan row(s)</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Couldn't run the check. Try again.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}